import type { ImportSourceStatus, ImportStatRow } from "../../../api/client.ts";
import { humanize } from "../../../utils/format.ts";
import type { StatusKind } from "../../StatusIcon.tsx";

// Source names that humanize() gets wrong (acronyms, brand casing, Nordic letters).
const SOURCE_LABELS: Record<string, string> = {
  apitube: "APITube",
  news_feed: "News feeds",
  ted: "TED",
  doffin: "Doffin",
  uhm: "UHM",
  nve: "NVE",
  einnsyn: "eInnsyn",
  mfn: "MFN",
  ntb: "NTB",
  nasdaq: "Nasdaq",
  cordis: "CORDIS",
  forskningsradet: "Forskningsrådet",
  nav: "NAV",
  brreg: "Brreg",
  brreg_roles: "Brreg roles",
  brreg_kunngjoringer: "Kunngjøringer",
  prh_notices: "PRH notices",
  prh: "PRH",
  bolagsverket_arsredovisningar: "Annual reports (SE)",
  crm_hubspot: "HubSpot",
  getleads: "GetLeads",
  vero: "Vero",
  ecb: "ECB",
};

export function sourceLabel(source: string): string {
  return SOURCE_LABELS[source] ?? humanize(source);
}

/** The `platform_import_stats` rows of one domain, optionally of some sources. */
export function statRows(stats: ImportStatRow[], domain: string, sources?: string[]): ImportStatRow[] {
  return stats.filter((r) => r.domain === domain && (!sources || sources.includes(r.source)));
}

type Count = "total" | "matched" | "added_24h" | "added_7d" | "refreshed_24h";

export function sumOf(rows: ImportStatRow[], field: Count): number {
  return rows.reduce((n, r) => n + (r[field] ?? 0), 0);
}

export function latestYear(rows: ImportStatRow[]): number | null {
  const years = rows.map((r) => r.latest_year).filter((y): y is number => y != null);
  return years.length ? Math.max(...years) : null;
}

/** Per source, summed over countries, largest first. */
export function bySource(rows: ImportStatRow[]): { source: string; total: number; added_24h: number }[] {
  const totals = new Map<string, { source: string; total: number; added_24h: number }>();
  for (const r of rows) {
    const t = totals.get(r.source) ?? { source: r.source, total: 0, added_24h: 0 };
    t.total += r.total;
    t.added_24h += r.added_24h ?? 0;
    totals.set(r.source, t);
  }
  return [...totals.values()].sort((a, b) => b.total - a.total);
}

/** Where a signal data type's records are counted in `platform_import_stats`. */
export const SIGNAL_STAT_DOMAINS: Record<string, { domain: string; sources?: string[] }> = {
  news_article: { domain: "news_article" },
  press_release: { domain: "press_release" },
  public_record: { domain: "public_record" },
  regulatory_filing: { domain: "regulatory_filing" },
  tender: { domain: "tender" },
  research_grant: { domain: "research_grant" },
  job_ad: { domain: "job_ad" },
  company_event: { domain: "registry_event" },
  notion_page: { domain: "notion", sources: ["page"] },
};

/** An import's status as the health icon and label every connection uses. */
export function importStatus(status: ImportSourceStatus): { kind: StatusKind; label: string } {
  switch (status) {
    case "ok":
      return { kind: "ok", label: "Up to date" };
    case "running":
      return { kind: "loading", label: "Running" };
    case "failed":
      return { kind: "error", label: "Failed" };
    case "overdue":
      return { kind: "attention", label: "Overdue" };
    case "paused":
      return { kind: "off", label: "Paused" };
    case "disabled":
      return { kind: "off", label: "Turned off" };
    case "never":
      return { kind: "off", label: "No runs yet" };
  }
}

export const NEEDS_ATTENTION: ImportSourceStatus[] = ["failed", "overdue"];
